import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Tabs} from '@dropbox/dig-components/tabs';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {useQuery} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {
  EditorDoc,
  EditorDocAdmin,
  EditordocService,
  EditorDocTemplateType,
  PlanningCycle,
} from 'client';
import {Layout} from 'components/DSYS/Layout';
import {Column, Table} from 'components/DSYS/Table';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {canViewEditorDocumentAdmin, canViewLivePlanningAdmin} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {useMemo, useState} from 'react';
import {useNavigate} from 'react-router';
import {getService} from 'utilities';
import {planningDocColumns, PlanningDocTableRow} from 'views/planning/PlanningDocTableRow';
import {isHardcodedTemplate} from 'views/planning/utils';

import {AdminPlanningLiveContent} from './AdminPlanningLive';

const comparePlanningCycleForSort = (a: EditorDoc, b: EditorDoc): number => {
  const aComplete = a.planning_year != null && a.planning_cycle != null;
  const bComplete = b.planning_year != null && b.planning_cycle != null;
  if (!aComplete && !bComplete) {
    return 0;
  }
  if (!aComplete) {
    return 1;
  }
  if (!bComplete) {
    return -1;
  }
  if (a.planning_year !== b.planning_year) {
    return (a.planning_year ?? 0) - (b.planning_year ?? 0);
  }
  if (a.planning_cycle === b.planning_cycle) {
    return 0;
  }
  return a.planning_cycle === PlanningCycle.H1 ? -1 : 1;
};

export const adminDocumentColumns: Column<EditorDoc>[] = [
  {
    ...planningDocColumns[0],
    sort: (a, b) => (a.name || '').localeCompare(b.name || '', undefined, {sensitivity: 'base'}),
  },
  {
    type: 'created_on',
    minWidth: 140,
    width: 170,
    skeleton: planningDocColumns[1].skeleton,
    sort: (a, b) => Date.parse(a.created_at) - Date.parse(b.created_at),
  },
  {
    type: 'document_body_updated',
    minWidth: 140,
    width: 170,
    skeleton: planningDocColumns[1].skeleton,
    sort: (a, b) => {
      const parse = (doc: EditorDoc) => {
        const iso = (doc as EditorDocAdmin).document_updated_at;
        return iso ? Date.parse(iso) : NaN;
      };
      const pa = parse(a);
      const pb = parse(b);
      const na = Number.isNaN(pa);
      const nb = Number.isNaN(pb);
      if (na && nb) {
        return 0;
      }
      if (na) {
        return 1;
      }
      if (nb) {
        return -1;
      }
      return pa - pb;
    },
  },
  {
    type: 'planning_cycle',
    minWidth: 160,
    width: 200,
    skeleton: () => (
      <Box>
        <Skeleton.Text width={100} />
      </Box>
    ),
    sort: comparePlanningCycleForSort,
  },
  {
    ...planningDocColumns[2],
    sort: (a, b) => String(a.status).localeCompare(String(b.status)),
  },
  {
    type: 'yjs_size',
    minWidth: 120,
    width: 150,
    skeleton: () => (
      <Box>
        <Skeleton.Text width={80} />
      </Box>
    ),
    sort: (a, b) => {
      const aSize = (a as EditorDocAdmin).yjs_decompressed_size_bytes ?? 0;
      const bSize = (b as EditorDocAdmin).yjs_decompressed_size_bytes ?? 0;
      return aSize - bSize;
    },
  },
  planningDocColumns[3],
];

const ADMIN_DOC_TAB_IDS = ['narrative', 'switchboard', 'template', 'live'] as const;
type AdminDocTabId = (typeof ADMIN_DOC_TAB_IDS)[number];

const getAdminDocTabId = (doc: EditorDocAdmin): AdminDocTabId => {
  if (isHardcodedTemplate(doc.id)) {
    return 'template';
  }
  if (doc.template_type === EditorDocTemplateType.NARRATIVE) {
    return 'narrative';
  }
  return 'switchboard';
};

export const AdminDocuments = () => {
  const [selectedTab, setSelectedTab] = useState<AdminDocTabId>('narrative');

  const {data, isLoading} = useQuery({
    queryKey: ['planning', 'documents', 'all'],
    queryFn: () => getService(EditordocService).getEmployeeDocsAdminApiV1EditordocAllGet(),
  });

  const navigate = useNavigate();

  const pulseUser = useAtomValue(pulseUserAtom);
  const canViewLiveTab = canViewLivePlanningAdmin(pulseUser);

  useDocumentTitle('Documents');

  const sortedDocs = useMemo(() => (data?.length ? [...data] : []), [data]);

  const narrativeDocs = useMemo(
    () => sortedDocs.filter((doc) => getAdminDocTabId(doc) === 'narrative'),
    [sortedDocs]
  );
  const switchboardDocs = useMemo(
    () => sortedDocs.filter((doc) => getAdminDocTabId(doc) === 'switchboard'),
    [sortedDocs]
  );
  const templateDocs = useMemo(
    () => sortedDocs.filter((doc) => getAdminDocTabId(doc) === 'template'),
    [sortedDocs]
  );

  const handleTabSelection = (tabId: string) => {
    if (tabId === 'live' && !canViewLiveTab) {
      return;
    }
    if (ADMIN_DOC_TAB_IDS.includes(tabId as AdminDocTabId)) {
      setSelectedTab(tabId as AdminDocTabId);
    }
  };

  const documentsTable = (docs: EditorDocAdmin[]) => (
    <Table
      columns={adminDocumentColumns}
      data={docs}
      sortConfig={{
        columnId: 'document_body_updated',
        asc: false,
      }}
    >
      <Table.Header />

      <Table.Body>
        {isLoading ? (
          <Table.Loading rowCount={5} />
        ) : docs.length === 0 ? (
          <Table.Row>
            <Table.Cell colSpan={adminDocumentColumns.length}>
              <Box paddingY="16">
                <Text color="faint">{t('admin_documents_tab_empty')}</Text>
              </Box>
            </Table.Cell>
          </Table.Row>
        ) : (
          <Table.Rows
            renderRow={(doc) => (
              <PlanningDocTableRow
                doc={doc}
                dbId={doc.db_id}
                documentId={doc.document?.id ?? null}
                showYjsSize
                yjsSize={doc.yjs_decompressed_size_bytes}
                splitDateAndPlanningCycle
                documentBodyUpdatedAtIso={doc.document_updated_at ?? null}
                enablePlanningCycleModal={
                  doc.template_type === EditorDocTemplateType.NARRATIVE &&
                  !isHardcodedTemplate(doc.id)
                }
              />
            )}
          />
        )}
      </Table.Body>
    </Table>
  );

  if (!pulseUser) {
    return null;
  }

  if (!canViewEditorDocumentAdmin(pulseUser)) {
    navigate('/');
    return null;
  }

  return (
    <Layout.Container
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {children: 'Documents', to: ROUTE_PATHS.ADMIN_DOCUMENTS},
      ]}
      width="100%"
    >
      <Stack gap="24">
        <Title size={24}>Documents</Title>

        <Tabs selectedTab={selectedTab} onSelection={handleTabSelection}>
          <Tabs.Group hasHorizontalPadding={false}>
            <Tabs.Tab id="narrative">{t('admin_documents_tab_narrative')}</Tabs.Tab>
            <Tabs.Tab id="switchboard">{t('admin_documents_tab_switchboard')}</Tabs.Tab>
            <Tabs.Tab id="template">{t('admin_documents_tab_template')}</Tabs.Tab>
            {canViewLiveTab ? <Tabs.Tab id="live">Live</Tabs.Tab> : null}
          </Tabs.Group>
          <Tabs.Panel tabId="narrative">
            <Box paddingTop="16">
              {selectedTab === 'narrative' ? documentsTable(narrativeDocs) : null}
            </Box>
          </Tabs.Panel>
          <Tabs.Panel tabId="switchboard">
            <Box paddingTop="16">
              {selectedTab === 'switchboard' ? documentsTable(switchboardDocs) : null}
            </Box>
          </Tabs.Panel>
          <Tabs.Panel tabId="template">
            <Box paddingTop="16">
              {selectedTab === 'template' ? documentsTable(templateDocs) : null}
            </Box>
          </Tabs.Panel>
          {canViewLiveTab ? (
            <Tabs.Panel tabId="live">
              <Box paddingTop="16">
                {selectedTab === 'live' ? <AdminPlanningLiveContent /> : null}
              </Box>
            </Tabs.Panel>
          ) : null}
        </Tabs>
      </Stack>
    </Layout.Container>
  );
};
